import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import {
    FormBox,
    ButtonGroup,
    Wrapper,
    Description,
    Title,
    TitleText,
    BackButton,
    Controls,
    SubmitButton,
} from './password-step.styles';
import { ArrowsArrowLeft } from '@vtbhome/juicyfront/indexIcon';
import { PasswordFormKeys } from '../../config/form-password-constants';
import { Field, Form } from 'react-final-form';
import { useLoginSelector, useLoginStepSelector } from '../../model/selectors';
import { useDispatch } from 'react-redux';
import { setLogin, setLoginStep } from '../../model/slice';
import { LoginSteps } from '../../model/types';
import { validate } from '../../lib/form-password-validate/form-password-validate';
import { useLazyCurrentUserQuery } from '@entities/current-user/api/credentials-user-api';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { useLoginByTemporaryPasswordMutation, useRequestPasswordMutation } from '../../api/login-api';
import { ROUTE_ADMIN_MAIN } from '@shared/config/routes/routes';
import { InputPassword } from '@shared/ui/input-password/input-password';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { RequestPasswordBtn } from '../request-password-btn/request-password-btn';
import { LoginByTemporaryPasswordResponse } from '../../api/login-types';
import { SmartCaptcha, SmartCaptchaProps } from '@yandex/smart-captcha';
import { SubscriptionStatus } from './types';
import { CAPTCHA_SITE_KEY } from './constants';
import { PasswordStepProps } from './types';
import { useRedirect } from '@shared/lib/hooks/use-redirect/use-redirect';

export const PasswordStep: React.FC<PasswordStepProps> = ({ showCaptcha = false }) => {
    const redirect = useRedirect();
    const dispatch = useDispatch();
    const inputRef = useRef<HTMLLabelElement | null>(null);
    const [getUser, userResult] = useLazyCurrentUserQuery();
    const user = useCurrentUserSelector();

    const currentStep = useLoginStepSelector();
    const email = useLoginSelector();

    const [login, loginResult] = useLoginByTemporaryPasswordMutation();
    const [requestPassword, requestPasswordResult] = useRequestPasswordMutation();

    const [, setToken] = useState('');
    const [, setStatus] = useState<SubscriptionStatus>('default');
    const [resetCaptcha, setResetCaptcha] = useState(0);
    const [isVisibleCaptcha, setIsVisibleCaptcha] = useState(false);

    const isSendingForm = loginResult.isLoading || userResult.isLoading;
    const isRequestingPassword = requestPasswordResult.isLoading;
    const formError = getQueryError(requestPasswordResult) || getQueryError(loginResult);

    const handlePrevStep = () => {
        dispatch(setLoginStep(LoginSteps.Login));
    };

    const handleRequestPassword = () => {
        if (!email) {
            return;
        }
        requestPassword({ email });
    };

    const handleSubmit = async (fields: Record<PasswordFormKeys, string>) => {
        // if (!email || (formError?.status === 401 && !token)) {
        //     return;
        // }

        if (!email) {
            return;
        }

        const response = (await login({ email, password: fields[PasswordFormKeys.Password] })) as {
            data: LoginByTemporaryPasswordResponse;
        };

        // Случай когда второй раз вводим неверный пароль,
        // тогда сбрасываем капчу и получаем новый токен
        // if (response?.error?.status === 401 && token) {
        //     setResetCaptcha((prev) => prev + 1);
        //     setToken('');
        // }

        if (response?.data) {
            getUser();
        }
    };

    const handleSuccess = useCallback((token: string) => {
        setToken(token);
        setStatus('success');
        requestPasswordResult.reset();
    }, []);

    const handleTokenExpired = useCallback(() => {
        setToken('');
        setStatus('token-expired');
        setResetCaptcha((prev) => prev + 1);
    }, []);

    const handleNetworkError: SmartCaptchaProps['onNetworkError'] = useCallback(() => setStatus('network-error'), []);

    const handleJavaScriptError: SmartCaptchaProps['onJavascriptError'] = useCallback((error) => {
        setStatus('javascript-error');
        console.warn(error);
    }, []);

    useLayoutEffect(() => {
        if (loginResult.isSuccess && user) {
            redirect({ url: ROUTE_ADMIN_MAIN });
            dispatch(setLoginStep(LoginSteps.Login));
            dispatch(setLogin());
        }
    }, [loginResult.isSuccess, user]);

    useEffect(() => {
        if (formError?.status === 401 && showCaptcha) {
            setIsVisibleCaptcha(true);
        }
    }, [formError?.status, showCaptcha]);

    useEffect(() => {
        const input = inputRef.current;
        if (input) {
            input.focus();
        }

        return () => {
            loginResult.reset();
        };
    }, [currentStep]);

    if (currentStep !== LoginSteps.Password) {
        return null;
    }

    return (
        <Wrapper>
            <Title>
                <BackButton buttonType='icon' size='m' onClick={handlePrevStep}>
                    <ArrowsArrowLeft />
                </BackButton>
                <TitleText size='h4'>Авторизация</TitleText>
            </Title>
            <Description size='subtitle1'>
                На e-mail (<b>{email}</b>) был направлен временный пароль для входа в приложение.
            </Description>
            <Form
                subscription={{}}
                onSubmit={handleSubmit}
                validate={validate}
                render={(form) => (
                    <FormBox onSubmit={form.handleSubmit} isVisibleCaptcha={isVisibleCaptcha}>
                        <Field
                            name={PasswordFormKeys.Password}
                            render={({ input, meta }) => (
                                <InputPassword
                                    ref={inputRef}
                                    label='Пароль'
                                    placeholder='Введите пароль'
                                    name={input.name}
                                    value={input.value}
                                    error={(meta.touched && meta.error) || formError?.message}
                                    disabled={isSendingForm}
                                    onChange={input.onChange}
                                />
                            )}
                        />
                        <Controls>
                            <ButtonGroup>
                                <RequestPasswordBtn
                                    disabled={isSendingForm || isRequestingPassword}
                                    preloader={isRequestingPassword}
                                    isSuccessRequestedAgain={requestPasswordResult.isSuccess}
                                    onClick={handleRequestPassword}
                                />
                                <SubmitButton
                                    type='submit'
                                    size='m'
                                    fullWidth={false}
                                    disabled={isSendingForm}
                                    preloader={isSendingForm}>
                                    {!isSendingForm && 'Подтвердить'}
                                </SubmitButton>
                            </ButtonGroup>
                        </Controls>
                        {showCaptcha && (
                            <SmartCaptcha
                                sitekey={CAPTCHA_SITE_KEY}
                                key={resetCaptcha}
                                // временно test, т.к. токен временный
                                test={true}
                                language='ru'
                                onNetworkError={handleNetworkError}
                                onJavascriptError={handleJavaScriptError}
                                onSuccess={handleSuccess}
                                onTokenExpired={handleTokenExpired}
                            />
                        )}
                    </FormBox>
                )}
            />
        </Wrapper>
    );
};
